import { getLocale } from 'umi';
import { isEmpty } from '@intelligence/v3/utils/common';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { LANGUAGE_CN } from '@/pages/home/_config/config';

import type { IGenreRes, IMainGenre } from '@intelligence/types/common';
import type { MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';
import type {
  IGenreSource,
  IGenreSourcesGenreOptions,
  IOptionWithChildren,
} from '@intelligence/components/GenreSelect/types';
import type { TDimension } from '@intelligence/v3/pages/CasualGenreProfile/types';

/**
 * 品类筛选入参的字段映射
 * 键：品类的数据源
 * 值：要筛选的品类的入参字段
 */
const genreValuesKeyConfigMap: IRecord<string> = {
  iegg: 'iegg_genre',
  sensortower: 'sensortower_genre',
  appmagic: 'app_magic',
  annie: 'appannie_genre',
};

/**
 * 品类信息的取值字段映射
 * 键：品类的数据源
 * 值：品类的取值字段
 */
const genreInformationKeyConfigMap: IRecord<{ main: string; sub: string }> = {
  iegg: {
    main: 'iegg_genre_req',
    sub: 'iegg_sub_genre',
  },
  sensortower: {
    main: 'sensortower_genre',
    sub: 'sensortower_sub_genre',
  },
  appmagic: {
    main: 'super_genre',
    sub: 'major_category',
  },
  annie: {
    main: 'genre',
    sub: 'sub_genre',
  },
};

export const GENRE_SOURCE_MAP: IRecord<IGenreSource> = {
  ieg: 'iegg',
  sensortower: 'sensortower',
  app_magic: 'appmagic',
  annir: 'annie',
  appannie: 'annie',
};

export const GENRE_SOURCE_REVERSE_MAP: IRecord = {
  iegg: 'ieg',
  sensortower: 'sensortower',
  appmagic: 'app_magic',
  annie: 'annir',
  appannie: 'annir',
};

/**
 * 统一转换 genreSource 的别名到标准值
 */
export const normalizeGenreSource = (genreSource?: string): IGenreSource | '' => {
  if (!genreSource) {
    return '';
  }

  return GENRE_SOURCE_MAP[genreSource] || (genreSource as IGenreSource);
};

/**
 * 在指定可用集合内安全获取 genreSource，不命中时返回兜底值
 */
export const getSafeGenreSource = <T extends string>(
  genreSource: string | undefined,
  validGenreSources: readonly T[],
  fallback: T | '' = '',
): T | '' => {
  const normalizedGenreSource = normalizeGenreSource(genreSource);

  if (validGenreSources.includes(normalizedGenreSource as T)) {
    return normalizedGenreSource as T;
  }

  return fallback;
};

export type TIeggGenreReqItem = {
  value?: string;
  children?: Array<{ value?: string }>;
};

export const GENRE_KEY_MAP: IRecord<string> = {
  iegg__mainGenre: 'iegg_genre_req',
  iegg__subGenre: 'iegg_sub_genre',
  sensortower__mainGenre: 'sensortower_genre',
  sensortower__subGenre: 'sensortower_sub_genre',
  annie__mainGenre: 'genre',
  annie__subGenre: 'sub_genre',
  appmagic__superGenre: 'super_genre',
  appmagic__majorCategory: 'major_category',
  appmagic__midCategory: 'mid_category',
  appmagic__subCategory: 'sub_category',
  appmagic__minorCategory: 'minor_category',
};

const pickGenreCellText = (value: unknown, placeholder = '-') => {
  if (value === null || value === undefined) {
    return placeholder;
  }
  const text = String(value).trim();
  return text || placeholder;
};

/** 筛选器/接口 genre_source → 表格品类列数据源 */
export const normalizeTableGenreSource = (genreSource?: string): IGenreSource => {
  const raw = (genreSource || '').toLowerCase().trim();
  if (!raw) {
    return 'sensortower';
  }
  const fromResKey = GENRE_SOURCE_MAP[raw as keyof Partial<IGenreRes>];
  if (fromResKey) {
    return fromResKey;
  }
  if (raw === 'appannie' || raw === 'data.ai') {
    return 'annie';
  }
  if (raw === 'app_magic') {
    return 'appmagic';
  }
  if (['iegg', 'sensortower', 'annie', 'appmagic', 'weixin'].includes(raw)) {
    return raw as IGenreSource;
  }
  return 'sensortower';
};

/** 表格行 App Magic 品类（兼容根级字段与 app_magic 嵌套） */
export const resolveAppMagicTableGenre = (record?: Record<string, any> | null) => {
  const nested = record?.app_magic;
  const source = nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : record || {};
  return {
    super_genre: source?.super_genre || '',
    major_category: source?.major_category || '',
    mid_category: source?.mid_category || '',
    sub_category: source?.sub_category || '',
  };
};

/** 从行数据取 IEGG 品类（兼容 iegg_genre_req 与旧字段名 iegg_genre 上的同结构数组） */
export const resolveIeggGenreReqArray = (record?: Record<string, any> | null): TIeggGenreReqItem[] | null => {
  if (!record) {
    return null;
  }
  if (Array.isArray(record.iegg_genre_req) && record.iegg_genre_req.length > 0) {
    return record.iegg_genre_req;
  }
  if (Array.isArray(record.iegg_genre) && record.iegg_genre.length > 0) {
    return record.iegg_genre;
  }
  return null;
};

/** iegg_genre_req 数组 → 表格展示用主/副品类字符串（| 分隔，与 annie 等源一致） */
export const formatIeggGenreReqToDisplayStrings = (
  req?: TIeggGenreReqItem[] | null,
): { mainGenre: string; subGenre: string } => {
  if (!Array.isArray(req) || req.length === 0) {
    return { mainGenre: '', subGenre: '' };
  }
  const mainList: string[] = [];
  const subList: string[] = [];
  req.forEach((item) => {
    const main = item?.value?.trim();
    if (main) {
      mainList.push(main);
    }
    const children = item?.children;
    if (Array.isArray(children)) {
      children.forEach((child) => {
        const sub = child?.value?.trim();
        if (sub) {
          subList.push(sub);
        }
      });
    }
  });
  return {
    mainGenre: mainList.join('|'),
    subGenre: subList.join('|'),
  };
};

/** 按当前筛选品类源解析表格主/副品类展示值 */
export const resolveTableGenreValues = (
  record: Record<string, any> | null | undefined,
  genreSource?: string,
  placeholder = '-',
  fieldOverride?: { mainGenreField?: string; subGenreField?: string },
): { mainGenre: string; subGenre: string; genreSource: IGenreSource } => {
  const source = normalizeTableGenreSource(genreSource);

  // 优先使用显式指定的主/副品类字段取值（部分榜单接口固定下发 genre/sub_genre 字段，
  // 与筛选器 genre_source 无关），未指定时再按 genre_source 推断字段。
  const { mainGenreField, subGenreField } = fieldOverride || {};
  if (mainGenreField || subGenreField) {
    return {
      mainGenre: pickGenreCellText(mainGenreField ? record?.[mainGenreField] : '', placeholder),
      subGenre: pickGenreCellText(subGenreField ? record?.[subGenreField] : '', placeholder),
      genreSource: source,
    };
  }

  if (source === 'iegg') {
    const fromReq = formatIeggGenreReqToDisplayStrings(resolveIeggGenreReqArray(record));
    if (fromReq.mainGenre || fromReq.subGenre) {
      return {
        mainGenre: pickGenreCellText(fromReq.mainGenre, placeholder),
        subGenre: pickGenreCellText(fromReq.subGenre, placeholder),
        genreSource: source,
      };
    }
    return {
      mainGenre: pickGenreCellText(typeof record?.iegg_genre === 'string' ? record.iegg_genre : '', placeholder),
      subGenre: pickGenreCellText(record?.iegg_sub_genre, placeholder),
      genreSource: source,
    };
  }

  const keys = genreInformationKeyConfigMap[source];
  if (!keys) {
    return { mainGenre: placeholder, subGenre: placeholder, genreSource: source };
  }
  return {
    mainGenre: pickGenreCellText(record?.[keys.main], placeholder),
    subGenre: pickGenreCellText(record?.[keys.sub], placeholder),
    genreSource: source,
  };
};

/**
 * 转换接口返回的主、副品类Options集合为符合GenreSelect组件的数据格式
 * @param list 主、副品类Options集合（接口返回的）
 * @returns 主、副品类Options集合（符合GenreSelect组件要求的）
 */
const formatGenreOptions = (list?: IMainGenre[]): IOptionWithChildren[] => {
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    const itemData = item?.main_genre;
    const subList = item?.sub_genre?.map((subItem) => {
      return {
        value: subItem?.genre,
        text: subItem?.genre_en,
        textCN: subItem?.genre_cn,
      };
    });
    return {
      value: itemData?.genre,
      text: itemData?.genre_en,
      textCN: itemData?.genre_cn,
      children: subList || [],
    };
  });
};

/**
 * 转换 Roblox 扁平路径数组为嵌套树形结构
 * Roblox 原始数据同一条主品类会出现在多条路径里，按 main_genre.genre 聚合副品类
 */
const formatRobloxGenreOptions = (list?: IMainGenre[]): IOptionWithChildren[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  const treeMap = new Map<string, IOptionWithChildren>();

  list.forEach((item) => {
    const mainGenre = item?.main_genre;
    const mainKey = mainGenre?.genre;
    if (!mainKey) {
      return;
    }

    if (!treeMap.has(mainKey)) {
      treeMap.set(mainKey, {
        value: mainKey,
        text: mainGenre.genre_en || mainKey,
        textCN: mainGenre.genre_cn || mainKey,
        children: [],
      });
    }

    const mainNode = treeMap.get(mainKey)!;
    const subGenres = item?.sub_genre || [];

    subGenres.forEach((subGenre) => {
      const subKey = subGenre?.genre;
      if (!subKey) {
        return;
      }

      const subExists = mainNode.children?.some((child) => child?.value === subKey);
      if (!subExists) {
        mainNode.children!.push({
          value: subKey,
          text: subGenre.genre_en || subKey,
          textCN: subGenre.genre_cn || subKey,
        });
      }
    });
  });

  return sortOptionsTree(Array.from(treeMap.values()));
};

/**
 * appmagic品类数据格式化方法（返回格式为Agent侧要求的数据格式）
 * @param list - 原始品类数据
 * @return appmagic品类数据格式化方法（返回格式为Agent侧要求的数据格式）
 */
export const formatAppMagicGenres = (list?: any[] | null): IAgentGenreValue[] => {
  if (isEmpty(list)) {
    return [];
  }

  return list!.map((item) => {
    const { value, children } = item || {};

    return { name: value || '', children: formatAppMagicGenres(children) };
  });
};

/**
 * 按 text 字段排序（a-z，忽略大小写）
 */
const sortByText = (a: IOptionWithChildren, b: IOptionWithChildren): number => {
  const textA = a?.text?.toLowerCase() || '';
  const textB = b?.text?.toLowerCase() || '';
  if (textA?.includes('other')) {
    return 1;
  }
  if (textB?.includes('other')) {
    return -1;
  }
  return textA.localeCompare(textB);
};

/**
 * 递归对树形结构的每一级按 text 字段排序
 */
const sortOptionsTree = (options: IOptionWithChildren[]): IOptionWithChildren[] => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  options.sort(sortByText).map((option) => {
    return {
      ...option,
      children: option?.children ? sortOptionsTree(option.children) : [],
    };
  });

  return options;
};

export const getGenreKeyIntelligence = (genreSource: IGenreSource, genreDimension: TDimension): string => {
  const genreKey = `${genreSource}__${genreDimension}`;
  return GENRE_KEY_MAP[genreKey] || '';
};

/**
 * 递归在品类选项中查找指定 value 的选项
 * @param options 品类选项列表
 * @param value 品类值
 * @returns 匹配的品类选项，未找到返回 undefined
 */
const findGenreOption = (options: IOptionWithChildren[], value: string): IOptionWithChildren | undefined => {
  if (!Array.isArray(options) || options.length === 0) {
    return undefined;
  }

  for (const option of options) {
    if (option?.value === value) {
      return option;
    }
    if (option?.children && option.children.length > 0) {
      const found = findGenreOption(option.children, value);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

/**
 * 获取品类名称的翻译文本
 * @param params 参数对象
 * @param params.locale 当前语言环境（可选，默认从 umi 获取）
 * @param params.genreOptionsMap 品类选项数据 map，key 为数据源，value 为选项列表
 * @param params.genreSource 品类数据源（sensortower/appmagic/iegg/annie）
 * @param params.genreValue 品类值（如 sensortower_genre 的值）
 * @returns 品类翻译后的名称，未找到返回原品类值
 */
export const getGenreNameI18n = (params: {
  locale?: string;
  genreOptionsMap: IRecord<IOptionWithChildren[]>;
  genreSource: IGenreSource;
  genreValue: string;
}): string => {
  const { locale, genreOptionsMap, genreSource, genreValue } = params || {};

  if (!genreValue) {
    return '';
  }

  // 获取当前数据源的品类选项列表
  let options = genreOptionsMap?.[genreSource];

  // 没有选项数据，返回原值
  if (isEmpty(options)) {
    return genreValue;
  }

  // 如果仍然没有找到选项数据，尝试从所有选项中查找
  if (!Array.isArray(options) || options.length === 0) {
    // 获取第一个可用的选项列表（通常只会有一类数据源的选项）
    const allKeys = Object.keys(genreOptionsMap || {});
    for (const key of allKeys) {
      const candidateOptions = genreOptionsMap?.[key];
      if (Array.isArray(candidateOptions) && candidateOptions.length > 0) {
        // 尝试在这个选项列表中查找
        const matchedOption = findGenreOption(candidateOptions, genreValue);
        if (matchedOption) {
          options = candidateOptions;
          break;
        }
      }
    }
  }

  if (!Array.isArray(options) || options.length === 0) {
    // 没有选项数据，返回原值
    return genreValue;
  }

  // 在选项中查找匹配的品类
  const matchedOption = findGenreOption(options, genreValue);
  if (!matchedOption) {
    // 未找到匹配选项，返回原值
    return genreValue;
  }

  // 根据当前语言环境返回对应的文本
  const currentLocale = locale || getLocale();
  const isCn = currentLocale === LANGUAGE_CN;
  const result = isCn ? matchedOption.textCN || matchedOption.text : matchedOption.text;

  return result || '';
};

/**
 * 获取品类名称（支持翻译）
 * 扩展原有的 getGenreNameIntelligence 函数，支持翻译功能
 * @param params 参数对象
 * @param params.locale 当前语言环境（可选，默认从 umi 获取）
 * @param params.genreOptionsMap 品类选项数据 map（可选，不传则不进行翻译）
 * @param params.genreSource 品类数据源
 * @param params.genreDimension 品类维度
 * @param params.data 数据项
 * @returns 品类名称（翻译后或原值）
 */
export const getGenreNameIntelligenceWithI18n = (params: {
  locale?: string;
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  genreSource: IGenreSource;
  genreDimension: TDimension;
  data?: ICasualGenreProfileMetricData | null;
}): string => {
  const locale = params?.locale;
  const genreOptionsMap = params?.genreOptionsMap;
  const genreSource = params?.genreSource || '';
  const genreDimension = params?.genreDimension || '';
  const data = params?.data || null;

  if (isEmpty(data)) {
    return '';
  }

  const genreKey = getGenreKeyIntelligence(genreSource, genreDimension);
  const genreValue = (data?.[genreKey as keyof ICasualGenreProfileMetricData] as string) || '';

  if (!genreValue) {
    return '';
  }

  // 如果有品类选项 map，则进行翻译
  if (genreOptionsMap) {
    return getGenreNameI18n({
      locale,
      genreOptionsMap,
      genreSource,
      genreValue,
    });
  }

  // 没有选项数据，返回原值
  return genreValue;
};

/**
 * 追加 appmagic 的 5级品类（剧情二合）（数据说写死的，所以情报平台前端写死）
 * @param list 接口返回的 appmagic 品类选项数据
 */
const appendAppmagicNarrativeMerge2Genres = (list?: any[]) => {
  if (!Array.isArray(list)) {
    return;
  }

  const listTmp = [
    {
      main_genre: {
        genre: 'Casual',
        genre_cn: '休闲',
        genre_en: 'Casual',
      },
      sub_genre: [
        {
          genre: 'Puzzle',
          genre_cn: '益智',
          genre_en: 'Puzzle',
        },
      ],
      level_3_genre: [
        {
          genre: 'Merge',
          genre_cn: '合并',
          genre_en: 'Merge',
        },
      ],
      level_4_genre: [
        {
          genre: 'Merge-2',
          genre_cn: '二消合并',
          genre_en: 'Merge-2',
        },
      ],
      level_5_genre: [
        {
          genre: 'Merge-2: Complex Metagame',
          genre_cn: 'Merge-2: Complex Metagame',
          genre_en: 'Merge-2: Complex Metagame',
        },
      ],
    },
    {
      main_genre: {
        genre: 'Casual',
        genre_cn: '休闲',
        genre_en: 'Casual',
      },
      sub_genre: [
        {
          genre: 'Puzzle',
          genre_cn: '益智',
          genre_en: 'Puzzle',
        },
      ],
      level_3_genre: [
        {
          genre: 'Merge',
          genre_cn: '合并',
          genre_en: 'Merge',
        },
      ],
      level_4_genre: [
        {
          genre: 'Merge-2',
          genre_cn: '二消合并',
          genre_en: 'Merge-2',
        },
      ],
      level_5_genre: [
        {
          genre: 'Merge-2: Simple',
          genre_cn: 'Merge-2: Simple',
          genre_en: 'Merge-2: Simple',
        },
      ],
    },
    {
      main_genre: {
        genre: 'Casual',
        genre_cn: '休闲',
        genre_en: 'Casual',
      },
      sub_genre: [
        {
          genre: 'Puzzle',
          genre_cn: '益智',
          genre_en: 'Puzzle',
        },
      ],
      level_3_genre: [
        {
          genre: 'Merge',
          genre_cn: '合并',
          genre_en: 'Merge',
        },
      ],
      level_4_genre: [
        {
          genre: 'Merge-2',
          genre_cn: '二消合并',
          genre_en: 'Merge-2',
        },
      ],
      level_5_genre: [
        {
          genre: 'Merge-2: Other',
          genre_cn: 'Merge-2: Other',
          genre_en: 'Merge-2: Other',
        },
      ],
    },
  ];

  Array.prototype.push.apply(list, listTmp);
};

/**
 * 转换 App Magic 扁平数组为嵌套树形结构
 * App Magic 的原始数据是扁平数组，每条数据包含 main_genre、sub_genre、level_3_genre、level_4_genre、level_5_genre
 * 需要将其转换为递归嵌套的树形结构，支持 2-5 级动态层级
 * @param list App Magic 原始数据数组
 * @returns 嵌套的树形结构（每一级按 text 字段 a-z 排序）
 */
const formatAppMagicGenreOptions = (list?: any[]): IOptionWithChildren[] => {
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  // 使用 Map 存储树形结构，key 为完整路径
  const treeMap = new Map<string, IOptionWithChildren>();

  appendAppmagicNarrativeMerge2Genres(list);

  list.forEach((item) => {
    const mainGenre = item?.main_genre;
    const subGenres = item?.sub_genre || [];
    const level3Genres = item?.level_3_genre || [];
    const level4Genres = item?.level_4_genre || [];
    const level5Genres = item?.level_5_genre || [];

    // 主品类键
    const mainKey = mainGenre?.genre;
    if (!mainKey) {
      return;
    }

    // 1. 构建或获取主品类节点（第1层级）
    if (!treeMap.has(mainKey)) {
      treeMap.set(mainKey, {
        value: mainKey,
        text: mainGenre.genre_en || mainKey,
        textCN: mainGenre.genre_cn || mainKey,
        children: [],
      });
    }
    const mainNode = treeMap.get(mainKey)!;

    // 2. 处理第2层级（sub_genre）
    // 每条记录的 sub_genre 数组通常只有一个元素，表示这条路径的第2层
    const subGenre = subGenres[0];
    // 副品类键
    const subKey = subGenre?.genre;
    if (!subKey) return;

    let subNode = mainNode?.children?.find((c) => c?.value === subKey);
    if (!subNode) {
      subNode = {
        value: subKey,
        text: subGenre.genre_en || subKey,
        textCN: subGenre.genre_cn || subKey,
        // @ts-ignore
        children: [],
      };
      // @ts-ignore
      mainNode.children.push(subNode);
    }

    // 3. 处理第3层级（level_3_genre）
    const level3Genre = level3Genres[0];
    // 第三层品类的键
    const level3Key = level3Genre?.genre;
    if (!level3Key) return;

    // @ts-ignore
    let level3Node = subNode.children.find((c) => c?.value === level3Key);
    if (!level3Node) {
      level3Node = {
        value: level3Key,
        text: level3Genre.genre_en || level3Key,
        textCN: level3Genre.genre_cn || level3Key,
        children: [],
      };
      // @ts-ignore
      subNode.children.push(level3Node);
    }

    // 4. 处理第4层级（level_4_genre）
    const level4Genre = level4Genres[0];
    const level4Key = level4Genre?.genre;
    if (!level4Key) {
      return;
    }

    // @ts-ignore
    let level4Node = level3Node.children.find((c) => c?.value === level4Key);
    if (!level4Node) {
      level4Node = {
        value: level4Key,
        text: level4Genre.genre_en || level4Key,
        textCN: level4Genre.genre_cn || level4Key,
        children: [],
      };
      level3Node?.children?.push(level4Node);
    }

    // 5. 处理第5层级（level_5_genre）
    const level5Genre = level5Genres[0];
    const level5Key = level5Genre?.genre;
    if (level5Key && !level4Node.children?.some((c) => c?.value === level5Key)) {
      level4Node.children?.push({
        value: level5Key,
        text: level5Genre.genre_en || level5Key,
        textCN: level5Genre.genre_cn || level5Key,
        children: [],
      });
    }
  });

  // 对每一级按 text 字段 a-z 排序
  return sortOptionsTree(Array.from(treeMap.values()));
};

/**
 * 转换接口返回的品类数据源类型与主、副品类Options集合的map映射为符合GenreSelect组件的数据格式
 * 1、转换品类数据源类型ieg为标准的iegg、annir为标转的annie
 * 2、转换主、副品类Option为标转的Option格式
 * @param res 品类数据源类型与主、副品类Options集合的map映射（接口返回的）
 * @returns 品类数据源类型与主、副品类Options集合的map映射（符合GenreSelect组件要求的）
 */
export const formatGenreSourcesOptions = (res: IGenreRes): IGenreSourcesGenreOptions => {
  if (!res) return { iegg: [], annie: [] };
  const resKeys = Object.keys(res) as (keyof IGenreRes)[];
  return resKeys.reduce((current, next) => {
    if (!next) return current;
    const genreSource = GENRE_SOURCE_MAP[next] || next;

    // App Magic 数据源使用专门的转换函数
    const genreSourceOptions =
      next === 'app_magic'
        ? formatAppMagicGenreOptions(res[next])
        : next === 'roblox'
          ? formatRobloxGenreOptions(res[next])
          : formatGenreOptions(res[next]);

    return { ...current, [genreSource]: genreSourceOptions };
  }, {});
};

/**
 * @date 2025-01-09
 * @description 构建品类筛选的 filter 方法,用于从列表数据中筛选出符合指定品类信息条件的数据项
 * @author lv.chen
 * @param params - 筛选参数对象
 * @param params.genre_source - 品类的数据源 (iegg/annie/sensortower)
 * @param params[genreKey] - 对应数据源的品类 value 数组
 * @return 返回一个 filter 函数,用于判断数据项是否符合品类筛选条件
 */
export const buildGenreFilter = (params?: IRecord | null) => {
  const genreSource: IGenreSource = params?.genre_source || '';
  const genreValue: IGenreValue[] = params?.[genreValuesKeyConfigMap[genreSource]] || [];
  // 获取当前数据源对应的品类字段配置
  const genreInformationKeyConfig = genreInformationKeyConfigMap[genreSource] || { main: '', sub: '' };
  const mainGenreKey = genreInformationKeyConfig.main;
  const subGenreKey = genreInformationKeyConfig.sub;

  return <T extends IGenreInformation>(cvm?: T | null): boolean => {
    // 1. 如果数据项为空,则过滤掉
    if (!cvm) {
      return false;
    }

    // 2. 空值检查:如果没有品类筛选条件,则不进行筛选,保留所有数据
    if (!genreValue || genreValue.length === 0) {
      return true;
    }

    // 3. 如果字段配置不存在,则过滤掉
    if (!mainGenreKey || !subGenreKey) {
      return false;
    }

    // 4. 获取数据项的主品类和副品类值
    const itemMainGenre = cvm[mainGenreKey as keyof T] as string;
    const itemSubGenre = cvm[subGenreKey as keyof T] as string;

    // 5. 遍历用户勾选的品类条件,只要满足任意一个条件即可通过筛选(多个勾选品类之间是 OR 关系)
    return genreValue.some((genre) => {
      // 5.1 检查主品类是否匹配
      if (itemMainGenre !== genre.value) {
        return false;
      }

      // 5.2 主品类匹配后,检查副品类条件
      // 如果没有副品类限制(children 为空),则只要主品类匹配即可通过
      if (!genre.children || genre.children.length === 0) {
        return true;
      }

      // 5.3 有副品类限制时,需要进一步检查副品类匹配情况
      // 将数据项的副品类字符串按 "|" 分割成数组
      const itemSubGenres = itemSubGenre ? itemSubGenre.split('|') : [];
      // 获取用户勾选的副品类数组
      const selectedSubGenres = genre.children.map((child) => child.value);

      // 5.4 根据 relation 判断副品类匹配逻辑
      if (genre.relation === 'or') {
        // OR 关系:数据项的副品类中至少包含一个勾选的副品类即可
        return selectedSubGenres.some((subGenre) => itemSubGenres.includes(subGenre));
      }
      // AND 关系:数据项的副品类中必须包含所有勾选的副品类
      return selectedSubGenres.every((subGenre) => itemSubGenres.includes(subGenre));
    });
  };
};

/**
 * 扩展的 SubGenre 类型，包含 indeterminate 字段
 */
export interface SubGenreWithIndeterminate extends SubGenre {
  indeterminate?: boolean;
}

/**
 * 品类筛选器 genretor 的 value 类型
 */
export interface IGenretorValue {
  source: string;
  mainGenres: MainGenre[];
  subGenres: SubGenreWithIndeterminate[];
  level3Genres: SubGenreWithIndeterminate[];
  level4Genres: SubGenreWithIndeterminate[];
  level5Genres: SubGenre[];
}

/**
 * 品类点击类型枚举
 */
export enum GenreTagClickType {
  /** 一级品类 */
  SuperGenre = 'super_genre',
  /** 二级品类 */
  MajorGenre = 'major_genre',
  /** 三级品类 */
  MidGenre = 'mid_genre',
  /** 四级品类 */
  SubGenre = 'sub_genre',
  /** 五级品类 */
  MinorGenre = 'minor_genre',
}

/**
 * 在 options 中查找指定 value 的选项
 */
const findOptionInList = (optionList: IOptionWithChildren[], value: string): IOptionWithChildren | undefined => {
  return optionList?.find((opt) => opt?.value === value);
};

/**
 * 判断是否为全选状态（无 children 或 children 为空数组）
 */
const isFullSelected = (item: { children?: any[] }): boolean => {
  const children = item?.children;
  return !Array.isArray(children) || children.length === 0;
};

/**
 * 生成GenreSelectMiddleware组件需要的组件value
 * @param params - 参数对象
 * @param params.source - 品类数据源
 * @param params.sourceValue - 品类数据源的勾选品类
 * @param params.optionsMap - 品类选项数据map
 * @return GenreSelectMiddleware组件需要的组件value
 */
export const generateGenreSelectValue = (params: {
  source: IGenreSource;
  sourceValue: IGenreValue[];
  optionsMap: IRecord<IOptionWithChildren[]>;
}): IGenretorValue => {
  const { source, sourceValue, optionsMap } = params || {};
  const options = optionsMap?.[source] || [];

  const result: IGenretorValue = {
    source,
    mainGenres: [],
    subGenres: [],
    level3Genres: [],
    level4Genres: [],
    level5Genres: [],
  };

  if (!Array.isArray(options) || options.length === 0) {
    return result;
  }

  if (!Array.isArray(sourceValue) || sourceValue.length === 0) {
    return result;
  }

  /**
   * 递归展开 options 中的所有子品类（全选时使用）
   * @param options options 中的子品类列表
   * @param level 当前层级（2=subGenres, 3=level3Genres, 4=level4Genres, 5=level5Genres）
   * @param parentValue 第1层父级 value
   * @param parent2Value 第2层父级 value
   * @param parent3Value 第3层父级 value
   * @param parent4Value 第4层父级 value
   */
  const expandAllChildren = (
    options: IOptionWithChildren[] | undefined,
    level: number,
    parentValue: string,
    parent2Value?: string,
    parent3Value?: string,
    parent4Value?: string,
  ): void => {
    if (!Array.isArray(options) || options.length === 0) {
      return;
    }

    for (const option of options) {
      const optionValue = option?.value || '';
      const subOptions = option?.children;
      if (level === 2) {
        result.subGenres.push({
          value: optionValue,
          parentValue,
          checked: true,
        });
        // 继续递归展开下一层
        expandAllChildren(subOptions, 3, parentValue, optionValue);
      } else if (level === 3) {
        result.level3Genres.push({
          value: optionValue,
          parentValue,
          parent2Value,
          checked: true,
        });
        // 继续递归展开下一层
        expandAllChildren(subOptions, 4, parentValue, parent2Value, optionValue);
      } else if (level === 4) {
        result.level4Genres.push({
          value: optionValue,
          parentValue,
          parent2Value,
          parent3Value,
          checked: true,
        });
        expandAllChildren(subOptions, 5, parentValue, parent2Value, parent3Value, optionValue);
      } else if (level === 5) {
        result.level5Genres.push({
          value: optionValue,
          parentValue,
          parent2Value,
          parent3Value,
          parent4Value,
          checked: true,
        });
      }
    }
  };

  /**
   * 处理 value1 中的 children（递归）
   * @param children value1 中的 children 列表
   * @param optionChildren options 中对应的 children 列表
   * @param level 当前层级
   * @param parentValue 第1层父级 value
   * @param parent2Value 第2层父级 value
   * @param parent3Value 第3层父级 value
   * @param parent4Value 第4层父级 value
   */
  const processChildren = (
    children: Omit<IGenreValue, 'relation'>[],
    optionChildren: IOptionWithChildren[] | undefined,
    level: number,
    parentValue: string,
    parent2Value?: string,
    parent3Value?: string,
    parent4Value?: string,
  ): void => {
    if (!Array.isArray(children) || children.length === 0) {
      return;
    }

    for (const child of children) {
      const childValue = child?.value || '';
      const childChildren = child?.children;
      const optionItem = findOptionInList(optionChildren || [], childValue);
      const optionItemChildren = optionItem?.children;
      const fullSelected = isFullSelected(child);

      if (level === 2) {
        // subGenres 层级
        result.subGenres.push({
          value: childValue,
          parentValue,
          checked: true,
        });

        if (fullSelected) {
          // 全选：从 options 展开所有子品类
          expandAllChildren(optionItemChildren, 3, parentValue, childValue);
        } else {
          // 半选：继续递归处理 children
          processChildren(childChildren || [], optionItemChildren, 3, parentValue, childValue);
        }
      } else if (level === 3) {
        // level3Genres 层级
        result.level3Genres.push({
          value: childValue,
          parentValue,
          parent2Value,
          checked: true,
        });

        if (fullSelected) {
          // 全选：从 options 展开所有子品类
          expandAllChildren(optionItemChildren, 4, parentValue, parent2Value, childValue);
        } else {
          // 半选：继续递归处理 children
          processChildren(childChildren || [], optionItemChildren, 4, parentValue, parent2Value, childValue);
        }
      } else if (level === 4) {
        result.level4Genres.push({
          value: childValue,
          parentValue,
          parent2Value,
          parent3Value,
          checked: true,
        });

        if (fullSelected) {
          expandAllChildren(optionItemChildren, 5, parentValue, parent2Value, parent3Value, childValue);
        } else {
          processChildren(
            childChildren || [],
            optionItemChildren,
            5,
            parentValue,
            parent2Value,
            parent3Value,
            childValue,
          );
        }
      } else if (level === 5) {
        result.level5Genres.push({
          value: childValue,
          parentValue,
          parent2Value,
          parent3Value,
          parent4Value,
          checked: true,
        });
      }
    }
  };

  // 遍历 sourceValue中的每个主品类
  for (const mainGenreItem of sourceValue) {
    const mainGenreItemValue = mainGenreItem?.value || '';
    const optionItem = findOptionInList(options, mainGenreItemValue);
    const optionItemChildren = optionItem?.children;
    const fullSelected = isFullSelected(mainGenreItem);

    // 添加到 mainGenres
    result.mainGenres.push({
      value: mainGenreItemValue,
      relation: mainGenreItem?.relation || Relation.Or,
      checked: fullSelected,
      indeterminate: !fullSelected,
    });

    if (fullSelected) {
      // 全选：从 options 展开所有子品类
      expandAllChildren(optionItemChildren, 2, mainGenreItemValue);
    } else {
      // 半选：递归处理 children
      processChildren(mainGenreItem?.children || [], optionItemChildren, 2, mainGenreItemValue);
    }
  }

  return result;
};

/**
 * 生成GenreSelectMiddleware组件需要的组件value
 * @param optionsMap - 品类选项数据map
 * @param source - 品类数据源
 * @param superGenre - 一级品类
 * @param majorGenre - 二级品类
 * @param midGenre - 三级品类
 * @param subGenre - 四级品类
 * @param minorGenre - 五级品类
 * @return GenreSelectMiddleware组件需要的组件value
 */
export const generateGenreSelectValueSimple = (
  optionsMap: IRecord<IOptionWithChildren[]>,
  source: string,
  superGenre: string,
  majorGenre?: string,
  midGenre?: string,
  subGenre?: string,
  minorGenre?: string,
): IGenretorValue => {
  const sourceValue: IGenreValue[] = [];

  if (superGenre) {
    const superGenreObj: IGenreValue = {
      value: superGenre,
      relation: Relation.Or,
      children: [],
    };

    if (majorGenre) {
      const majorGenreObj: Omit<IGenreValue, 'relation'> = {
        value: majorGenre,
        children: [],
      };

      if (midGenre) {
        const midGenreObj: Omit<IGenreValue, 'relation'> = {
          value: midGenre,
          children: [],
        };

        if (subGenre) {
          const subGenreObj: Omit<IGenreValue, 'relation'> = {
            value: subGenre,
            children: [],
          };

          if (minorGenre) {
            const ssubGenreObj: Omit<IGenreValue, 'relation'> = {
              value: minorGenre,
            };

            subGenreObj!.children!.push(ssubGenreObj);
          }

          midGenreObj!.children!.push(subGenreObj);
        }

        majorGenreObj!.children!.push(midGenreObj);
      }

      superGenreObj!.children!.push(majorGenreObj);
    }

    sourceValue.push(superGenreObj);
  }

  return generateGenreSelectValue({
    source,
    sourceValue,
    optionsMap,
  });
};

/**
 * 生成GenreSelectMiddleware组件需要的组件value
 * @param optionsMap - 品类选项数据map
 * @param genretorValue - 品类筛选器value
 * @param source - 品类数据源
 * @param level - 品类层级
 * @param genre - 品类
 * @return GenreSelectMiddleware组件需要的组件value
 */
export const generateGenreSelectValueIntelligence = (
  optionsMap: IRecord<IOptionWithChildren[]>,
  /** 品类筛选器value */
  genretorValue: IRecord<IOptionWithChildren[]>,
  source: string,
  level: number,
  genre: string,
): IGenretorValue => {
  const options = optionsMap?.[source] || [];
  const mainGenres = genretorValue?.mainGenres || [];
  const subGenres = genretorValue?.subGenres || [];
  const level3Genres = genretorValue?.level3Genres || [];
  const level4Genres = genretorValue?.level4Genres || [];
  const sourceValue: IGenreValue[] = [];

  /**
   * 根据品类层级，从 options 中找到对应品类并构建父级链路
   * level=1: 一级品类
   * level=2: 二级品类，需要找到其父级
   * level=3: 三级品类，需要找到其父级和祖父级
   * level=4: 四级品类，需要找到其父级、祖父级和曾祖父级
   * level=5: 五级品类，需要找到其父级、祖父级、曾祖父级和高祖父级
   */

  if (level === 1) {
    // 一级品类：直接添加，不带 children（表示全选）
    sourceValue.push({
      value: genre,
      relation: Relation.Or,
    });
  } else if (level === 2) {
    // 二级品类：需要找到其父级
    for (const option of options) {
      const optionValue = option?.value || '';
      if (mainGenres?.length > 0) {
        const isMainGenreChecked = mainGenres?.find((mainGenre) => {
          return mainGenre?.value === optionValue;
        });
        if (!isMainGenreChecked) {
          continue;
        }
      }
      const subOption = findOptionInList(option?.children || [], genre);
      if (subOption) {
        sourceValue.push({
          value: optionValue,
          relation: Relation.Or,
          children: [{ value: genre }],
        });
        // break;
      }
    }
  } else if (level === 3) {
    // 三级品类：需要找到其祖父级和父级
    for (const option of options) {
      const optionValue = option?.value || '';
      if (mainGenres?.length > 0) {
        const isMainGenreChecked = mainGenres?.find((mainGenre) => {
          return mainGenre?.value === optionValue;
        });
        if (!isMainGenreChecked) {
          continue;
        }
      }
      for (const subOption of option?.children || []) {
        const subOptionValue = subOption?.value || '';
        if (subGenres?.length > 0) {
          const isSubGenreChecked = subGenres?.find((subGenre) => {
            // @ts-ignore
            return subGenre?.parentValue === optionValue && subGenre?.value === subOptionValue;
          });
          if (!isSubGenreChecked) {
            continue;
          }
        }
        const level3Option = findOptionInList(subOption?.children || [], genre);
        if (level3Option) {
          sourceValue.push({
            value: optionValue,
            relation: Relation.Or,
            children: [
              {
                value: subOptionValue,
                children: [{ value: genre }],
              },
            ],
          });
          // break;
        }
      }
      // if (sourceValue.length > 0) break;
    }
  } else if (level === 4) {
    // 四级品类：需要找到其曾祖父级、祖父级和父级
    for (const option of options) {
      const optionValue = option?.value || '';
      if (mainGenres?.length > 0) {
        const isMainGenreChecked = mainGenres?.find((mainGenre) => {
          return mainGenre?.value === optionValue;
        });
        if (!isMainGenreChecked) {
          continue;
        }
      }
      for (const subOption of option?.children || []) {
        const subOptionValue = subOption?.value || '';
        if (subGenres?.length > 0) {
          const isSubGenreChecked = subGenres?.find((subGenre) => {
            // @ts-ignore
            return subGenre?.parentValue === optionValue && subGenre?.value === subOptionValue;
          });
          if (!isSubGenreChecked) {
            continue;
          }
        }
        for (const level3Option of subOption?.children || []) {
          const level3OptionValue = level3Option?.value || '';
          if (level3Genres?.length > 0) {
            const isLevel3GenreChecked = level3Genres?.find((level3Genre) => {
              return (
                // @ts-ignore
                level3Genre?.parentValue === optionValue &&
                // @ts-ignore
                level3Genre?.parent2Value === subOptionValue &&
                level3Genre?.value === level3OptionValue
              );
            });
            if (!isLevel3GenreChecked) {
              continue;
            }
          }
          const level4Option = findOptionInList(level3Option?.children || [], genre);
          if (level4Option) {
            sourceValue.push({
              value: optionValue,
              relation: Relation.Or,
              children: [
                {
                  value: subOptionValue,
                  children: [
                    {
                      value: level3OptionValue,
                      children: [{ value: genre }],
                    },
                  ],
                },
              ],
            });
            // break;
          }
        }
        // if (sourceValue.length > 0) break;
      }
      // if (sourceValue.length > 0) break;
    }
  } else if (level === 5) {
    // 五级品类：需要找到其高祖父级、曾祖父级、祖父级和父级
    for (const option of options) {
      const optionValue = option?.value || '';
      if (mainGenres?.length > 0) {
        const isMainGenreChecked = mainGenres?.find((mainGenre) => {
          return mainGenre?.value === optionValue;
        });
        if (!isMainGenreChecked) {
          continue;
        }
      }
      for (const subOption of option?.children || []) {
        const subOptionValue = subOption?.value || '';
        if (subGenres?.length > 0) {
          const isSubGenreChecked = subGenres?.find((subGenre) => {
            // @ts-ignore
            return subGenre?.parentValue === optionValue && subGenre?.value === subOptionValue;
          });
          if (!isSubGenreChecked) {
            continue;
          }
        }
        for (const level3Option of subOption?.children || []) {
          const level3OptionValue = level3Option?.value || '';
          if (level3Genres?.length > 0) {
            const isLevel3GenreChecked = level3Genres?.find((level3Genre) => {
              return (
                // @ts-ignore
                level3Genre?.parentValue === optionValue &&
                // @ts-ignore
                level3Genre?.parent2Value === subOptionValue &&
                level3Genre?.value === level3OptionValue
              );
            });
            if (!isLevel3GenreChecked) {
              continue;
            }
          }
          for (const level4Option of level3Option?.children || []) {
            const level4OptionValue = level4Option?.value || '';
            if (level4Genres?.length > 0) {
              const isLevel4GenreChecked = level4Genres?.find((level4Genre) => {
                return (
                  // @ts-ignore
                  level4Genre?.parentValue === optionValue &&
                  // @ts-ignore
                  level4Genre?.parent2Value === subOptionValue &&
                  // @ts-ignore
                  level4Genre?.parent3Value === level3OptionValue &&
                  level4Genre?.value === level4OptionValue
                );
              });
              if (!isLevel4GenreChecked) {
                continue;
              }
            }
            const level5Option = findOptionInList(level4Option?.children || [], genre);
            if (level5Option) {
              sourceValue.push({
                value: optionValue,
                relation: Relation.Or,
                children: [
                  {
                    value: subOptionValue,
                    children: [
                      {
                        value: level3OptionValue,
                        children: [
                          {
                            value: level4OptionValue,
                            children: [{ value: genre }],
                          },
                        ],
                      },
                    ],
                  },
                ],
              });
            }
          }
        }
      }
    }
  }

  return generateGenreSelectValue({
    source,
    sourceValue,
    optionsMap,
  });
};
